import React, { createRef, forwardRef, useEffect, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { useAnchor } from '$contexts/anchor';
import slugify from '$utilities/slugify';

/* =========================================================================  */
/* Component(s) */
/* =========================================================================  */

/**
 * @typedef {PropTypes.InferProps<typeof propTypes>} Props
 */
const AnchorNavigation = forwardRef(({
    anchors,
    className,
    containerClassName
}, fwdRef) => {
    const { current } = useAnchor();
    const [ elRefs, setElRefs ] = useState([]);
    const [ showArrow, setShowArrow ] = useState(false);
    const [ hasOverflow, setHasOverflow ] = useState(false);
    const wrapperRef = useRef();
    const scrollTimeout = useRef();

    useEffect(() => {
        if(anchors) {
            setElRefs((elRefs) =>
                Array(anchors.length)
                    .fill()
                    .map((_, i) => elRefs[i] || createRef()),
            );
        }
    }, [anchors?.length]);

    useEffect(() => {
        if (current) {
            const index = anchors?.findIndex(anchor => slugify(anchor.title) === current);

            if(index !== -1) {
                if (scrollTimeout.current) {
                    clearTimeout(scrollTimeout.current);
                }

                // wait for document scroll to finish
                // work around for Chromium issue 1299237
                // https://bugs.chromium.org/p/chromium/issues/detail?id=1299237
                scrollTimeout.current = setTimeout(() => {
                    // scroll current anchor into view
                    wrapperRef.current.scroll(
                        {
                            top: 0,
                            left: index === 0 ? 0 : elRefs[index]?.current?.offsetLeft,
                            behavior: 'smooth',
                        }
                    );

                    // Hide or show view more right arrow
                    if ((index !== anchors.length - 1) && wrapperRef?.current?.clientWidth < wrapperRef?.current?.scrollWidth) {
                        setShowArrow(true);
                    } else {
                        setShowArrow(false);
                    }

                }, 500);
            }
        }
    }, [current]);

    useLayoutEffect(() => {
        if (wrapperRef?.current?.clientWidth < wrapperRef?.current?.scrollWidth) {
            setShowArrow(true);
            setHasOverflow(true);
        }

        const handleScroll = () => {
            const threshold = 5;
            if (wrapperRef.current.scrollLeft >= wrapperRef?.current?.scrollWidth - wrapperRef?.current?.clientWidth - threshold) {
                setShowArrow(false);
            }
            else {
                setShowArrow(true);
            }
        };

        wrapperRef?.current?.addEventListener('scroll', handleScroll);

        return () => {
            wrapperRef?.current?.removeEventListener('scroll', handleScroll);
        };
    }, [wrapperRef]);

    if (!anchors.length) { return null; }

    const handleScrollRight = () => {
        const index = anchors?.findIndex(anchor => anchor.title === current);
        if(index !== -1 && index !== anchors.length -1) {
            // Scroll to end
            wrapperRef.current.scroll(
                {
                    top: 0,
                    left: wrapperRef?.current?.scrollWidth,
                    behavior: 'smooth',
                }
            );
        }
    };

    const scrollToElementWithOffset = (id, offset) => {
        const element = document.getElementById(id);
        if (element) {
            const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
            const offsetPosition = elementPosition - offset;
            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });
        }
    };

    return (
        <div
            className='layout-container bg-white md:w-full sticky top-[44px] z-10 border-b'
            ref={fwdRef}>
            <div className='layout-grid-negative-gutter flex py-[12px] md:py-[23px]'>

                <div className='hidden md:block md:w-1/12 layout-grid-col-padding' />

                <div
                    className={`layout-grid-col-padding md:w-11/12 overflow-auto ${hasOverflow ? 'w-full' : 'w-auto'} ${containerClassName ? containerClassName : ''}`}
                    ref={wrapperRef}>
                    <div
                        className='w-fit anchor-navigation'>
                        <div className={`rounded-button w-fit flex whitespace-nowrap ${className ? className : ''}`}>
                            {anchors.map((anchor, idx) => {
                                return (
                                    <button
                                        className={`px-20 py-[17.5px] rounded type-style-5 mr-10 last:mr-0 ${slugify(anchor.title) === current ? 'type-style-5 border border-charcoal px-20 flex justify-center items-center w-fit h-[44px] rounded-[30px] cursor-pointer' : ''}`}
                                        key={`anchor-nav-${idx}`}
                                        onClick={() => scrollToElementWithOffset(`${slugify(anchor.title)}`, 100)}
                                        ref={elRefs[idx]}
                                        type={'button'}>
                                        <a
                                            className='type-style-5'>
                                            <span className='mr-10'>{idx + 1}</span>{anchor.title}
                                        </a>
                                    </button>
                                );
                            })}
                        </div>
                        <button
                            className={`rounded border-t border-r border-b border-border-grey h-full absolute top-0 right-0 -mr-30 pl-50 pr-20 bg-gradient-to-l from-white from-10% via-white to-100% hidden ${showArrow ? 'md:block' : ''}`}
                            onClick={handleScrollRight}
                            type='button'>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
});

/* =========================================================================  */
/* Typing and export(s) */
/* =========================================================================  */

AnchorNavigation.displayName = 'AnchorNavigation';

const propTypes = {
    anchors: PropTypes.array,
    className: PropTypes.string,
    containerClassName: PropTypes.string,
};

AnchorNavigation.propTypes = propTypes;

export default AnchorNavigation;
